/**
 * RecipeRegisterPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  Editor,
  InputFormTable,
  UploadFile,
  Text,
} from 'components';
import {
  Avatar,
  Flex,
  Spin,
  Table,
  Tag,
  Radio,
  Input,
  Space,
  Button,
  Image,
  Select,
} from 'antd';
import { stringToMoneyFormat, STORE_URL } from 'utils';
import { APPLICATION_STATUS, PRODUCT_STATUS } from 'constants/ProductConstants';
import { SearchOutlined, MinusOutlined } from '@ant-design/icons';
const RecipeRegisterPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    INITIAL_VALUE,
    inputValue,
    onInputValue,
    onChange = () => {},
    selectedList = [],
    onSelect = () => {},
    isLoading = false,
    products = [],
    categories = [],
    file = null,
    onFileChange = () => {},
    onSubmit = () => {},
    currentPage = 1,
    setCurrentPage = () => {},
    modalType,
    onOpen,
    searchText = '',
    setSearchText = () => {},
    searchedColumn = '',
    setSearchedColumn = () => {},
    data,
  } = props;

  /* ===== STATE ===== */

  const searchInput = React.useRef(null);
  const [tableKey, setTableKey] = React.useState(Date.now());
  const [pageSize, setPageSize] = React.useState(10);
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    subTitle: {
      fontSize: 17,
      fontWeight: 500,
      lineHeight: '141%',
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };
  /* ===== FUNCTION ===== */
  /**
   * 테이블 검색 함수
   * @param {*} selectedKeys
   * @param {*} confirm
   * @param {*} dataIndex
   */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });
  /**
   * 페이지 네이션 함수
   * @param {number} page
   * --
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * 테이블 초기화 함수
   * --
   */
  const handleResetFilters = () => {
    setSearchText('');
    setSearchedColumn('');
    setTableKey(Date.now()); // 새로운 키 부여 → Table 전체 리렌더
    if (searchInput.current) {
      searchInput.current.input.value = '';
    }
  };

  /**
   * SECTION: 기본 정보
   */
  const form1 = [
    {
      label: '레시피 명',
      form: [
        {
          input: (
            <Input
              size="large"
              placeholder="레시피 명 입력"
              style={{ ...styles.form.input.large, width: 800 }}
              value={inputValue.group_product_title}
              onChange={(e) => onChange('group_product_title', e.target.value)}
              count={{
                show: true,
                max: 50,
              }}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '레시피 설명',
      form: [
        {
          input: (
            <Input.TextArea
              size="large"
              placeholder="레시피 상품 입력"
              style={{ ...styles.form.input.large, width: 800 }}
              value={inputValue.group_product_description}
              onChange={(e) =>
                onChange('group_product_description', e.target.value)
              }
              rows={4}
              count={{
                show: true,
                max: 250,
              }}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '레시피',
      form: [
        {
          input: (
            <Editor
              placeholder={'레시피 입력해주세요'}
              initialContent={inputValue?.group_product_content}
              editorOnChange={(content) =>
                onChange('group_product_content', content)
              }
              height={430}
              width="800"
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '레시피 썸네일',
      form: [
        {
          input: (
            <Flex gap={12}>
              {inputValue?.thumbnail_url ? (
                <div style={{ position: 'relative', top: 0, left: 0 }}>
                  <Image
                    style={{ height: '130px' }}
                    src={`${STORE_URL}/${inputValue?.thumbnail_url}`}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<MinusOutlined />}
                    style={{ position: 'absolute', top: -10, right: -10 }}
                    onClick={() => onChange('thumbnail_url', null)}
                  />
                </div>
              ) : (
                <UploadFile
                  files={file ? [file] : []}
                  preview
                  onChange={(files) => onFileChange(files[0])}
                />
              )}
            </Flex>
          ),
        },
      ],
    },
    {
      label: '노출여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.is_show}
                onChange={(e) => onChange('is_show', e.target.value)}
              >
                <Radio value={'Y'}>노출함</Radio>
                <Radio value={'N'}>숨김</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '메인노출여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={inputValue.is_main}
                onChange={(e) => onChange('is_main', e.target.value)}
              >
                <Radio value={'Y'}>노출함</Radio>
                <Radio value={'N'}>숨김</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  /* ===== HOOKS ===== */
  /**
   * 테이블 컬럼
   * --
   */
  const columns = React.useMemo(() => {
    return [
      {
        key: 'product_name',
        dataIndex: 'product_name',
        title: '상품명',
        sorter: (a, b) => a.product_name.localeCompare(b.product_name, 'ko'),
        ...getColumnSearchProps('product_name'),
        render: (item, { thumbnail }) => (
          <Flex gap={8} align="center">
            <Avatar src={thumbnail} shape="square" size={64} />
            {item}
          </Flex>
        ),
      },
      {
        key: 'product_code',
        dataIndex: 'product_code',
        ...getColumnSearchProps('product_code'),
        title: '상품코드',
        sorter: (a, b) => a.product_code.localeCompare(b.product_code, 'ko'),
      },
      {
        key: 'product_category_name',
        dataIndex: 'product_category_name',
        title: '카테고리',
        sorter: (a, b) =>
          a.product_category_name.localeCompare(b.product_category_name, 'ko'),
        render: (product_category_name) => product_category_name,
        filters: categories.map((_item) => ({
          text: _item.product_category_name,
          value: _item.product_category_id,
        })),
        onFilter: (value, record) => record.product_category_id === value,
      },
      {
        key: 'price',
        dataIndex: 'price',
        title: '단가',
        sorter: (a, b) => a.price - b.price,
        render: (item) => stringToMoneyFormat(item, '원'),
      },
      {
        key: 'is_show',
        dataIndex: 'is_show',
        title: '노출여부',
        render: (item) => (
          <Tag color={item === 'Y' ? 'processing' : 'default'}>
            {item === 'Y' ? '노출' : '숨김'}
          </Tag>
        ),
        filters: [
          {
            text: '노출',
            value: 'Y',
          },
          {
            text: '숨김',
            value: 'N',
          },
        ],
        onFilter: (value, record) => record.is_show === value,
      },
      {
        key: 'product_status',
        dataIndex: 'product_status',
        title: '상품판매상태',
        render: (item) => (
          <Tag color={PRODUCT_STATUS[item]?.color}>
            {PRODUCT_STATUS[item]?.title}
          </Tag>
        ),
        filters: Object.values(PRODUCT_STATUS).map((item) => ({
          text: item.title,
          value: item.value,
        })),
        onFilter: (value, record) => record.product_status === value,
      },
      {
        key: 'application_status',
        dataIndex: 'application_status',
        title: '상품심사상태',
        render: (item) => (
          <Tag color={APPLICATION_STATUS[item]?.color}>
            {APPLICATION_STATUS[item]?.title}
          </Tag>
        ),
        filters: Object.entries(APPLICATION_STATUS).map(([key, value]) => ({
          text: value.title,
          value: key,
        })),
        onFilter: (value, record) => record.application_status === value,
      },
    ];
  }, [categories]);
  /**
   * 테이블 초기화 동작
   * --
   */
  React.useEffect(() => handleResetFilters(), [data]);

  const newProduct = React.useMemo(() => {
    let arr = products;
    if (searchText && searchedColumn) {
      arr = arr.filter((item) => {
        return item[searchedColumn]?.toString()?.includes(searchText);
      });
    }
    arr = [
      ...arr.filter((item) =>
        selectedList.some((select) => select.product_id === item.product_id),
      ),
      ...arr.filter(
        (item) =>
          !selectedList.some((select) => select.product_id === item.product_id),
      ),
    ];

    return arr;
  }, [searchText, searchedColumn, products, selectedList]);
  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row gutter={[16, 16]}>
            <Col x={24}>
              <Title style={{ ...styles.title }}>레시피 상품 등록/수정</Title>
            </Col>

            {/* SECTION: 그룹상품 정보 */}
            <Col x={24}>
              <Content maxWidth={'100%'} padding={24}>
                <Title style={{ ...styles.subTitle }}>기본 정보</Title>
                <InputFormTable form={form1} />
              </Content>
            </Col>
            {/* !SECTION: 그룹상품 정보 */}
            {/* SECTION: 상품 선택 */}
            <Col x={24}>
              <Content maxWidth={'100%'} padding={24}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 20,
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Flex gap={8}>
                    <Title style={{ ...styles.subTitle }}>상품 선택</Title>
                    <Text size={14}>
                      <span style={{ color: '#0066FF' }}>
                        {/* {console.log(selectedList)} */}
                        {selectedList?.length}
                      </span>
                      개의 상품 선택 됨
                    </Text>
                  </Flex>
                  <Flex gap={8}>
                    <Button
                      onClick={() => onSelect(newProduct, ...selectedList)}
                    >
                      검색된 항목 전체 선택
                    </Button>
                    <Button onClick={() => onSelect([])}>
                      검색된 항목 전체 선택해제
                    </Button>
                  </Flex>
                </div>
                <Table
                  key={tableKey}
                  rowKey={'product_id'}
                  columns={columns}
                  dataSource={newProduct}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    onChange: handlePageChange,
                    onShowSizeChange: (current, size) => setPageSize(size),
                    total: newProduct?.length || 0,
                  }}
                  scroll={{ x: 'max-content' }}
                  rowSelection={{
                    selectedRowKeys: selectedList.map(
                      (item) => item.product_id,
                    ),
                    onChange: (keys, rows) => {
                      const currentPageIds = newProduct.map(
                        (item) => item.product_id,
                      );

                      const preservedRows = selectedList.filter(
                        (item) => !currentPageIds.includes(item.product_id),
                      );
                      onSelect([...preservedRows, ...rows]);
                    },
                  }}
                />
                {/* 저장/취소 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 20,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => {
                      modalType === 'CREATE'
                        ? onSubmit('CREATE')
                        : onSubmit('UPDATE');
                    }}
                  >
                    {modalType === 'CREATE' ? '저장' : '수정'}
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={() => {
                      onSelect([]);
                      onFileChange('');
                      onInputValue(INITIAL_VALUE);
                      onOpen(false);
                    }}
                  >
                    취소
                  </Button>
                </div>
                {/* 저장/취소 버튼 끝 */}
              </Content>
            </Col>
            {/* !SECTION: 상품 선택 */}
          </Row>
        </Content>
      </Spin>
    </>
  );
};

export default RecipeRegisterPresenter;
